import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FormContainer } from '../../components';
import { useForm } from 'react-hook-form';
import useAuthHook from '../../api/auth';
import useAuth from '../../hooks/useAuth';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { InputEmail, InputPassword } from '../../utils/dynamicForm';
import useUserCategoriesHook from '../../api/panel/userCategories';
import { GoogleOauthID } from "../../config/client-config";

const GOOGLE_CLIENT_ID = GoogleOauthID;

const Login = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';     // 👈 pass to Google endpoint
  const pathName = searchParams.get('next') || '/admin';
  const navigate = useNavigate();

  const { auth, setAuth } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm();

  const { postLogin, postGoogleLogin } = useAuthHook();
  const { getAdminCategories } = useUserCategoriesHook({});
  const { refetch: refetchAdminCategories } = getAdminCategories;

  const { mutateAsync: loginMutateAsync, isLoading: isLoginLoading } = postLogin;
  const { mutateAsync: googleMutateAsync, isLoading: isGoogleLoading } = postGoogleLogin;

  // If already logged-in
  useEffect(() => {
    if (auth?.userInfo) navigate('/admin');
  }, [auth?.userInfo, navigate]);

  // (A) Optional dynamic loader for the Google script if you didn't add it in index.html
  const scriptLoaded = useRef(false);
  useEffect(() => {
    if (window.google?.accounts?.id) return; // already loaded
    if (scriptLoaded.current) return;

    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => initGoogle();
    document.body.appendChild(s);
    scriptLoaded.current = true;

    return () => {
      // Cleanup not strictly needed for this script
    };
  }, []);

  // (B) Initialize the Google button / one-tap
  const initGoogle = () => {
    if (!window.google?.accounts?.id || !GOOGLE_CLIENT_ID) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCredential, // gets Google ID token
      auto_select: false,               // change to true if you want “auto” behavior
      ux_mode: 'popup',                 // 'redirect' also possible
    });

    // Render button into a container
    const btn = document.getElementById('googleSignInDiv');
    if (btn) {
      window.google.accounts.id.renderButton(btn, {
        type: 'standard',
        shape: 'rectangular',
        theme: 'outline',
        text: 'continue_with',
        size: 'large',
        logo_alignment: 'left',
        width: '100%',
      });
    }

    // Optional: One Tap UI (comment out if you don’t want it)
    // window.google.accounts.id.prompt();
  };

  useEffect(() => {
    // If script already loaded via index.html, just init
    if (window.google?.accounts?.id) initGoogle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID]);

  // (C) Handle Google credential
  const handleGoogleCredential = async (response) => {
    try {
      const credential = response?.credential;
      if (!credential) return;

      const user = await googleMutateAsync({ credential, invite: inviteToken });
      if (user?.token) {
        localStorage.setItem('userInfo', JSON.stringify(user));
        setAuth({ userInfo: user });
        await refetchAdminCategories();
        navigate(pathName);
      }
    } catch (err) {
      console.error('Google login failed:', err);
    }
  };

  // Password login submit
  const submitHandler = async (data) => {
    try {
      const response = await loginMutateAsync(data);
      if (response?.token) {
        localStorage.setItem('userInfo', JSON.stringify(response));
        setAuth({ userInfo: response });
        await refetchAdminCategories();
        navigate(pathName);
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
      <FormContainer>
        <HelmetProvider>
          <Helmet>
            <title>Login</title>
            <meta property='og:title' content='Login' key='title'/>
          </Helmet>
        </HelmetProvider>

        <h3 className='fw-light font-monospace text-center'>{t('Sign In')}</h3>
        <script
            src="https://accounts.google.com/gsi/client"
            async
            defer
        ></script>
        {/* Classic email/password */}
        <form onSubmit={handleSubmit(submitHandler)}>
          {InputEmail({register, errors, label: 'Email', name: 'email', placeholder: 'Email'})}
          {InputPassword({register, errors, label: t('Password'), name: 'password', placeholder: t('Password')})}

          <button type='submit' className='btn btn-primary form-control' disabled={isLoginLoading}>
            {isLoginLoading ? <span className='spinner-border spinner-border-sm'/> : <div>{t('sign_in')}</div>}
          </button>
        </form>

        {/* Divider */}
        <div className='text-center my-3 text-muted'>— {t('or')} —</div>

        {/* Google Sign-In button target */}
        <div id='googleSignInDiv' style={{width: '100%'}}>
          {/* Google renders its button here */}
        </div>
        {isGoogleLoading && (
            <div className='d-flex justify-content-center mt-2'>
              <span className='spinner-border spinner-border-sm'/>
            </div>
        )}

        {/* Forgot password link */}
        <div className='row pt-3'>
          <div className='col'>
            <Link to='/admin/forgot-password' className='ps-1'>
              {t('Forgot Password')}?
            </Link>
          </div>
        </div>
      </FormContainer>
  );
};

export default Login;
